import {FC, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useQueryClient} from 'react-query'
import clsx from 'clsx'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useAuth} from '../../auth'
import {
  InputCurrency,
  InputHour,
  secondsToHoursFormatNotSeconds,
} from '../../../../_metronic/helpers'
import {Toast} from '../../../../_metronic/partials/widgets'
import {HelpTip} from '../../help/HelpTip'
import {initialWorkingDay, WorkingDayForm} from '../core/_models'
import {updateWorkingDay} from '../core/_requests'
import {ONBOARDING_QUERY_KEY} from '../hooks/useOnboardingProgress'

type Props = {
  show: boolean
  onHide: () => void
}

// Valor do dia da semana no backend (DAYOFWEEK do MySQL): domingo = 1 ... sábado = 7.
const WEEKDAYS = [
  {id: 'monday', value: '2', label: 'WEEKDAYS.MONDAY'},
  {id: 'tuesday', value: '3', label: 'WEEKDAYS.TUESDAY'},
  {id: 'wednesday', value: '4', label: 'WEEKDAYS.WEDNESDAY'},
  {id: 'thursday', value: '5', label: 'WEEKDAYS.THURSDAY'},
  {id: 'friday', value: '6', label: 'WEEKDAYS.FRIDAY'},
  {id: 'saturday', value: '7', label: 'WEEKDAYS.SATURDAY'},
  {id: 'sunday', value: '1', label: 'WEEKDAYS.SUNDAY'},
]

// Custo por hora e jornada de trabalho (antes era o passo "Gerenciando seus dados" do wizard).
// São a base do custo, da margem e do acompanhamento de horas dos projetos.
export const WorkingDayModal: FC<Props> = ({show, onHide}) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const {currentUser, setCurrentUser} = useAuth()

  const required = intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})
  const schema = Yup.object().shape({
    cost_hour: Yup.number().required(required).moreThan(0, required),
    shift_time: Yup.string().min(1, required).required(required),
    workdays: Yup.array().min(1, required).required(required),
  })

  const [initialValues] = useState<WorkingDayForm>({
    cost_hour: currentUser?.cost_hour || initialWorkingDay.cost_hour,
    shift_time: currentUser?.shift_time || initialWorkingDay.shift_time,
    workdays: currentUser?.workdays || initialWorkingDay.workdays,
  })

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      const item = intl.formatMessage({id: 'TABLE.GENERAL.LABEL.USER'})

      try {
        await updateWorkingDay(values)
        setCurrentUser({
          ...currentUser!,
          shift_time: values.shift_time,
          cost_hour: values.cost_hour,
          workdays: values.workdays,
        })
        await queryClient.invalidateQueries(ONBOARDING_QUERY_KEY)
        Toast.fire({
          icon: 'success',
          title: intl.formatMessage({id: 'ALERT.LABEL.SUCCESS_UPDATE_ITEM'}, {item}),
          customClass: {popup: 'bg-light-success', title: 'text-success'},
        })
        onHide()
      } catch (error) {
        console.error(error)
        Toast.fire({
          icon: 'error',
          iconHtml: '<span>!</span>',
          title: intl.formatMessage({id: 'ALERT.LABEL.ERROR_UPDATE_ITEM'}, {item}),
          customClass: {popup: 'bg-light-danger', title: 'text-danger'},
        })
      } finally {
        setSubmitting(false)
      }
    },
  })

  // Horas por semana = duração do turno x dias marcados.
  let shift = formik.values.shift_time == null ? '0' : formik.values.shift_time
  if (formik.values.shift_time?.length === 4) shift = '0' + formik.values.shift_time
  const shiftSeconds =
    parseInt(shift.substr(0, 2), 10) * 3600 + parseInt(shift.substr(3, 2), 10) * 60
  const hoursPerWeek = secondsToHoursFormatNotSeconds(shiftSeconds * formik.values.workdays.length)

  const fieldError = (name: 'cost_hour' | 'shift_time' | 'workdays') =>
    formik.touched[name] && formik.errors[name] ? (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block'>
          <span role='alert'>{formik.errors[name] as string}</span>
        </div>
      </div>
    ) : null

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName='onboarding-modal'>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>Seu custo por hora e sua jornada</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-gray-700 fs-6 mb-8'>
            Com esses dados o TaskRush calcula o custo e a margem dos projetos e acompanha as horas
            trabalhadas. Você pode alterar quando quiser em Meu Perfil.
          </p>

          <div className='fv-row mb-8'>
            <label htmlFor='cost_hour' className='d-flex align-items-center fs-6 fw-semibold mb-2'>
              <span className='required'>
                {intl.formatMessage({id: 'FORM.INPUT.NAME.COST_HOUR'})}
              </span>
              <span className='ms-2'>
                <HelpTip text='O valor hora é o custo de uma hora do seu trabalho: divida seus custos mensais (salário, despesas e impostos) pelas horas trabalhadas no mês.' />
              </span>
            </label>
            <InputCurrency
              {...formik.getFieldProps('cost_hour')}
              placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.VALUE'})}
              type='text'
              name='cost_hour'
              id='cost_hour'
              className={clsx(
                'form-control form-control-solid',
                {'is-invalid': formik.touched.cost_hour && formik.errors.cost_hour},
                {'is-valid': formik.touched.cost_hour && !formik.errors.cost_hour}
              )}
              autoComplete='off'
              disabled={formik.isSubmitting}
              onChange={(e: any) => {
                formik.setFieldValue('cost_hour', e.target.value.replace('.', '').replace(',', '.'))
              }}
            />
            {fieldError('cost_hour')}
          </div>

          <div className='fv-row mb-8'>
            <div className='fs-6 fw-semibold mb-2'>
              {intl.formatMessage({id: 'FORM.INPUT.NAME.WORK_DAYS'})}
            </div>
            <div className='onboarding-weekdays' role='group'>
              {WEEKDAYS.map((day) => (
                <span key={day.id}>
                  <input
                    type='checkbox'
                    className='btn-check'
                    id={day.id}
                    name='workdays'
                    value={day.value}
                    onChange={formik.handleChange}
                    checked={formik.values.workdays.includes(day.value)}
                  />
                  <label className='onboarding-weekday' htmlFor={day.id}>
                    {intl.formatMessage({id: day.label})}
                  </label>
                </span>
              ))}
            </div>
            {fieldError('workdays')}
          </div>

          <div className='fv-row'>
            <div className='row align-items-center g-3'>
              <div className='col-md-7'>
                <div className='input-group'>
                  <span className='input-group-text'>
                    {intl.formatMessage({id: 'FORM.INPUT.NAME.WORKDAYS'})}
                  </span>
                  <InputHour
                    {...formik.getFieldProps('shift_time')}
                    className={clsx(
                      'form-control form-control-solid',
                      {'is-invalid': formik.touched.shift_time && formik.errors.shift_time},
                      {'is-valid': formik.touched.shift_time && !formik.errors.shift_time}
                    )}
                    id='shift_time'
                    name='shift_time'
                    placeholder='00:00'
                    disabled={formik.isSubmitting}
                    onChange={(e: any) => {
                      const numbers = e.target.value.replace(/\D/g, '')
                      let hours = numbers.slice(0, -2) || '0'
                      const minutes = numbers.slice(-2).padStart(2, '0')
                      if (hours.length > 2) hours = hours.slice(-2)
                      formik.setFieldValue('shift_time', hours.padStart(2, '0') + ':' + minutes)
                    }}
                  />
                </div>
                {fieldError('shift_time')}
              </div>
              <div className='col-md-5'>
                <span className='fs-5 fw-bold'>
                  {intl.formatMessage(
                    {id: 'FORM.INPUT.NAME.HOURS_PER_WEEK'},
                    {
                      hours:
                        formik.values.workdays && formik.values.shift_time ? hoursPerWeek : '00:00',
                    }
                  )}
                </span>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onHide}>
            {intl.formatMessage({id: 'FORM.GENERAL.CANCEL_BUTTON'})}
          </button>
          <button
            type='submit'
            className='btn btn-primary dash-toolbar-btn-primary'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            <span className='indicator-label'>
              {intl.formatMessage({id: 'GENERAL.SAVE_BUTTON'})}
            </span>
            {formik.isSubmitting && (
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
